import { useMemo } from "react";
import { useLocation } from "wouter";
import { useUser } from "@clerk/react";
import { useListTasks, useListRecords, useGetProfile } from "@workspace/api-client-react";
import {
  CheckSquare, Circle, CheckCircle2, AlertTriangle, CalendarDays,
  Flag, ArrowRight, FileBox, ClipboardCheck, Clock, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { format, isToday, isPast, isTomorrow, parseISO } from "date-fns";

const PRIORITY_CONFIG = {
  high:   { label: "High",   dot: "bg-red-500",   badge: "bg-red-100 text-red-700" },
  medium: { label: "Medium", dot: "bg-amber-400", badge: "bg-amber-100 text-amber-700" },
  low:    { label: "Low",    dot: "bg-green-500", badge: "bg-green-100 text-green-700" },
} as const;

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function dueDateLabel(dateStr: string | null | undefined): { label: string; urgent: boolean } {
  if (!dateStr) return { label: "", urgent: false };
  const d = parseISO(dateStr);
  if (isToday(d)) return { label: "Due today", urgent: true };
  if (isTomorrow(d)) return { label: "Due tomorrow", urgent: false };
  if (isPast(d)) return { label: `Overdue · ${format(d, "d MMM")}`, urgent: true };
  return { label: `Due ${format(d, "d MMM")}`, urgent: false };
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
  onClick,
  actionLabel,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  accent: "blue" | "red" | "amber" | "green" | "gray";
  onClick?: () => void;
  actionLabel?: string;
}) {
  const colours = {
    blue:  { bg: "bg-blue-50",   icon: "text-blue-500",   value: "text-blue-700",   border: "border-blue-100" },
    red:   { bg: "bg-red-50",    icon: "text-red-500",    value: "text-red-700",    border: "border-red-100" },
    amber: { bg: "bg-amber-50",  icon: "text-amber-500",  value: "text-amber-700",  border: "border-amber-100" },
    green: { bg: "bg-green-50",  icon: "text-green-500",  value: "text-green-700",  border: "border-green-100" },
    gray:  { bg: "bg-gray-50",   icon: "text-gray-400",   value: "text-gray-700",   border: "border-gray-200" },
  }[accent];

  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`w-full text-left rounded-xl border p-3 sm:p-5 flex flex-col gap-2 sm:gap-3 transition-shadow ${colours.bg} ${colours.border} ${onClick ? "hover:shadow-md cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-start justify-between">
        <div className={`p-2 rounded-lg bg-white/60 ${colours.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
        {onClick && actionLabel && (
          <span className={`text-xs font-medium ${colours.value} flex items-center gap-1`}>
            {actionLabel} <ArrowRight className="h-3 w-3" />
          </span>
        )}
      </div>
      <div>
        <div className={`text-2xl sm:text-3xl font-bold ${colours.value}`}>{value}</div>
        <div className="text-sm text-gray-500 mt-0.5">{label}</div>
      </div>
    </button>
  );
}

// ─── Task Row ─────────────────────────────────────────────────────────────────

function TaskRow({ task }: { task: any }) {
  const priority = (task.priority ?? "medium") as keyof typeof PRIORITY_CONFIG;
  const { label: dueLabel, urgent } = dueDateLabel(task.dueDate);

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="mt-0.5 flex-shrink-0">
        {task.completed
          ? <CheckCircle2 className="h-4 w-4 text-green-500" />
          : <Circle className="h-4 w-4 text-gray-300" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${task.completed ? "line-through text-gray-400" : "text-gray-800"}`}>
          {task.title}
        </p>
        {dueLabel && (
          <p className={`text-xs mt-0.5 flex items-center gap-1 ${urgent ? "text-red-500" : "text-gray-400"}`}>
            <Clock className="h-3 w-3" /> {dueLabel}
          </p>
        )}
      </div>
      <div className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${PRIORITY_CONFIG[priority].badge}`}>
        {PRIORITY_CONFIG[priority].label}
      </div>
    </div>
  );
}

// ─── Dashboard page ───────────────────────────────────────────────────────────

export default function Dashboard() {
  const [, setLocation] = useLocation();
  const { user } = useUser();
  const { data: profileData } = useGetProfile();
  const profileFirstName = (profileData as any)?.name?.trim().split(" ")[0];
  const firstName = user?.firstName || profileFirstName || "";

  const { data: allTasks = [], isLoading: tasksLoading } = useListTasks();
  const { data: allRecords = [], isLoading: recordsLoading } = useListRecords();

  const tasks = allTasks as any[];
  const records = allRecords as any[];

  // Task breakdowns
  const incompleteTasks = useMemo(
    () => tasks.filter((t) => !t.completed),
    [tasks]
  );

  const todayTasks = useMemo(
    () => incompleteTasks.filter((t) => t.dueDate && isToday(parseISO(t.dueDate))),
    [incompleteTasks]
  );

  const overdueTasks = useMemo(
    () => incompleteTasks.filter((t) => t.dueDate && isPast(parseISO(t.dueDate)) && !isToday(parseISO(t.dueDate))),
    [incompleteTasks]
  );

  // Records not yet in Sentral
  const notEnteredRecords = useMemo(
    () => records.filter((r) => !r.enteredIntoSentral),
    [records]
  );

  // Show top tasks by urgency: overdue first, then due today, then the rest sorted by priority
  const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
  const previewTasks = useMemo(() => {
    return [...incompleteTasks]
      .sort((a, b) => {
        const aOverdue = a.dueDate && isPast(parseISO(a.dueDate)) && !isToday(parseISO(a.dueDate));
        const bOverdue = b.dueDate && isPast(parseISO(b.dueDate)) && !isToday(parseISO(b.dueDate));
        const aToday = a.dueDate && isToday(parseISO(a.dueDate));
        const bToday = b.dueDate && isToday(parseISO(b.dueDate));
        const aScore = aOverdue ? 0 : aToday ? 1 : 2;
        const bScore = bOverdue ? 0 : bToday ? 1 : 2;
        if (aScore !== bScore) return aScore - bScore;
        return (priorityOrder[a.priority ?? "medium"] ?? 1) - (priorityOrder[b.priority ?? "medium"] ?? 1);
      })
      .slice(0, 8);
  }, [incompleteTasks]);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6 sm:space-y-8">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {getGreeting()}{firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="text-gray-500 mt-1">
            {format(new Date(), "EEEE, d MMMM yyyy")}
          </p>
        </div>
        <Button onClick={() => setLocation("/app/records/new")} className="gap-2 flex-shrink-0" data-write-action>
          <Plus className="h-4 w-4" /> New Record
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tasksLoading ? (
          <>
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </>
        ) : (
          <>
            <StatCard
              icon={CalendarDays}
              label="Tasks due today"
              value={todayTasks.length}
              accent={todayTasks.length > 0 ? "blue" : "gray"}
              onClick={() => setLocation("/app/tasks")}
              actionLabel="View"
            />
            <StatCard
              icon={AlertTriangle}
              label="Overdue tasks"
              value={overdueTasks.length}
              accent={overdueTasks.length > 0 ? "red" : "gray"}
              onClick={overdueTasks.length > 0 ? () => setLocation("/app/tasks") : undefined}
              actionLabel={overdueTasks.length > 0 ? "View" : undefined}
            />
          </>
        )}

        {recordsLoading ? (
          <>
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </>
        ) : (
          <>
            <StatCard
              icon={FileBox}
              label="Records not yet uploaded"
              value={notEnteredRecords.length}
              accent={notEnteredRecords.length > 0 ? "amber" : "gray"}
              onClick={() => setLocation("/app/records")}
              actionLabel="View records"
            />
            <StatCard
              icon={ClipboardCheck}
              label="Total records"
              value={records.length}
              accent="gray"
              onClick={() => setLocation("/app/records")}
              actionLabel="View all"
            />
          </>
        )}
      </div>

      {/* Records action banner — only when there are unsynced records */}
      {!recordsLoading && notEnteredRecords.length > 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-amber-100 rounded-lg p-2.5 flex-shrink-0">
              <FileBox className="h-5 w-5 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-amber-900">
                {notEnteredRecords.length} record{notEnteredRecords.length !== 1 ? "s" : ""} pending upload to school system
              </p>
              <p className="text-sm text-amber-700 mt-0.5">
                These records have been created but not yet marked as uploaded to your school system.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className="flex-shrink-0 w-full sm:w-auto border-amber-300 bg-white text-amber-800 hover:bg-amber-100 gap-2"
            onClick={() => setLocation("/app/records")}
          >
            <ArrowRight className="h-4 w-4" /> View records
          </Button>
        </div>
      )}

      {/* Main two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* To-do list overview (takes 2/3) */}
        <Card className="lg:col-span-2 shadow-sm border-gray-200">
          <CardHeader className="pb-3 flex flex-row items-center justify-between border-b">
            <div className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4 text-primary" />
              <CardTitle className="text-base font-semibold text-gray-800">Tasks</CardTitle>
              {incompleteTasks.length > 0 && (
                <Badge variant="secondary" className="text-xs px-2 py-0.5">
                  {incompleteTasks.length} open
                </Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-primary hover:text-primary gap-1"
              onClick={() => setLocation("/app/tasks")}
            >
              View all <ArrowRight className="h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent className="px-4 py-0">
            {tasksLoading ? (
              <div className="space-y-3 py-4">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full rounded" />
                ))}
              </div>
            ) : previewTasks.length === 0 ? (
              <div className="py-12 text-center">
                <CheckCircle2 className="h-10 w-10 text-green-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">All caught up!</p>
                <p className="text-sm text-gray-400 mt-1">No open tasks right now.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 gap-2"
                  onClick={() => setLocation("/app/tasks")}
                  data-write-action
                >
                  <Plus className="h-3.5 w-3.5" /> Add a task
                </Button>
              </div>
            ) : (
              <div>
                {previewTasks.map((task) => (
                  <TaskRow key={task.id} task={task} />
                ))}
                {incompleteTasks.length > 8 && (
                  <div className="py-3 text-center">
                    <button
                      onClick={() => setLocation("/app/tasks")}
                      className="text-xs text-primary hover:underline font-medium"
                    >
                      +{incompleteTasks.length - 8} more tasks — view all
                    </button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick links panel (1/3) */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Quick links</h2>
          {[
            { label: "Diary",     sub: "Today's lesson plan",       href: "/app/diary",    color: "text-blue-600",   bg: "bg-blue-50" },
            { label: "Lessons",   sub: "Browse lesson plans",       href: "/app/lessons",  color: "text-violet-600", bg: "bg-violet-50" },
            { label: "Students",  sub: "Student directory",         href: "/app/students", color: "text-teal-600",   bg: "bg-teal-50" },
            { label: "Analytics", sub: "Compliance & progress insights", href: "/app/analytics", color: "text-orange-600", bg: "bg-orange-50" },
          ].map((link) => (
            <button
              key={link.href}
              onClick={() => setLocation(link.href)}
              className="w-full text-left rounded-xl border border-gray-200 bg-white hover:shadow-md transition-shadow p-4 flex items-center gap-3"
            >
              <div className={`w-9 h-9 rounded-lg ${link.bg} flex items-center justify-center flex-shrink-0`}>
                <ArrowRight className={`h-4 w-4 ${link.color}`} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800">{link.label}</p>
                <p className="text-xs text-gray-400 truncate">{link.sub}</p>
              </div>
            </button>
          ))}
        </div>

      </div>
    </div>
  );
}
